import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type { RxHistoryResponse } from "../types";

const POLLING_INTERVAL_MS = 2000;
const MAX_POLLS = 15;
const MAX_EMPTY_FINISHED_POLLS = 3;

export enum RxHistoryStatus {
  FINISHED = "FINISHED",
  SENT = "SENT",
}

interface UseRxHistoryParams {
  patientId: string;
  enabled?: boolean;
}

const shouldKeepPolling = (
  pages: RxHistoryResponse[] | undefined,
  dataUpdateCount: number,
) => {
  if (pages === undefined || pages.length > 1) return false;

  const firstPage = pages[0];
  const isFinished = firstPage?.status === RxHistoryStatus.FINISHED;
  const hasResults = (firstPage?.rxHistory?.length ?? 0) > 0;

  if (isFinished && hasResults) return false;
  if (isFinished && !hasResults)
    return dataUpdateCount <= MAX_EMPTY_FINISHED_POLLS;
  return dataUpdateCount <= MAX_POLLS;
};

export const useRxHistory = ({
  patientId,
  enabled = true,
}: UseRxHistoryParams) => {
  const queryClient = useQueryClient();
  const queryKey =
    QueryKeys.Patient.MedicalRecords.RxHistory.DEFAULT(patientId);

  const query = useInfiniteQuery<RxHistoryResponse>({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const response = await axiosInstance.get<RxHistoryResponse>(
        "/rx/medications/rx-history",
        {
          params: {
            patientId,
            pageNumber: pageParam,
          },
        },
      );
      return response.data;
    },
    enabled: enabled && !!patientId,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.hasNextPage) {
        return lastPage.pageNumber + 1;
      }
      return undefined;
    },
    refetchInterval: (query) => {
      // Stop polling when we have results (FINISHED with data), multiple pages
      // are loaded, or max polls reached. Continue polling if FINISHED but empty
      // since DoseSpot may return FINISHED with no results before actual data arrives.
      return shouldKeepPolling(
        query.state.data?.pages,
        query.state.dataUpdateCount,
      )
        ? POLLING_INTERVAL_MS
        : false;
    },
  });

  const dataUpdateCount =
    queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  const isPollingActive = shouldKeepPolling(query.data?.pages, dataUpdateCount);

  return { ...query, isPollingActive };
};
